/**
 * RNR PDP — sections 1 & 2 behaviour.
 *
 * Loaded once, from sections/main-product-rnr.liquid only (section 2 has no
 * <script src> of its own — see docs/rnr-pdp-plan.md §5). The theme editor
 * can re-execute this tag when the section reloads, so state that must
 * survive a re-run lives on `window.__rnrPdp`, not in this closure.
 *
 * Everything touching section 1 (gallery, plan cards, accordions) is bound
 * with ONE delegated `click` listener on `document`. <product-rerender> does
 * a full `replaceWith()` of that whole subtree on every plan change (no
 * `allow-partial-rerender` — see plan §0), so any `el.addEventListener`
 * bound directly to a gallery thumb/arrow/accordion header goes dead the
 * moment a customer picks a plan. Never do that here.
 */
(function () {
  window.__rnrPdp = window.__rnrPdp || {};
  var state = window.__rnrPdp;

  function setActiveSlide(gallery, index) {
    if (!gallery) return;
    index = String(index);
    gallery.querySelectorAll('[data-rnr-slide]').forEach(function (slide) {
      slide.classList.toggle('is-active', slide.getAttribute('data-rnr-slide') === index);
    });
    gallery.querySelectorAll('[data-rnr-thumb]').forEach(function (thumb) {
      thumb.classList.toggle('is-active', thumb.getAttribute('data-rnr-thumb') === index);
    });
  }

  /* ---- Plan switch replay ----
     In the prototype the two plan cards are plain state, so picking the other
     one animates both at once: the open body collapses 1fr -> 0fr while the
     picked one expands 0fr -> 1fr, over .5s, alongside the border, dot, price
     and "+" transitions.

     Here the cards are the real <variant-picker> radios. Picking an unselected
     one fires `change`, and <product-rerender> replaces the whole hero subtree
     — measured at ~30ms after the click. Nothing can transition in 30ms, and
     the replacement cards are BRAND NEW elements that arrive already in their
     final state, so a newly-inserted element has no previous computed style to
     animate from. Result: the panel snaps. That is the "jerky" report.

     So: remember the outgoing state, wait for the swap, rewind the fresh cards
     into that old state with transitions suppressed for one frame, then let go.
     The transition then runs on the new DOM exactly as the prototype's does.

     `shopify:section:load` does NOT fire for <product-rerender> on the
     storefront (checked — only the theme editor gets it), so the swap is
     detected by watching the clicked card fall out of the document. */
  function replayPlanSwitch(prevOpenValue) {
    var host = document.querySelector('.rnr-pdp-plans');
    var cards = host ? host.querySelectorAll('.rnr-pdp-plan') : [];
    if (!cards.length) return;

    // Capture where the new DOM wants to end up, then rewind to where it was.
    var target = [];
    host.classList.add('is-replaying');
    cards.forEach(function (card) {
      var radio = card.querySelector('.rnr-pdp-plan__radio');
      var value = radio ? radio.getAttribute('data-rr-plan-value') : '';
      var wasThisOne = normPlan(value) === normPlan(prevOpenValue);
      target.push([card.classList.contains('is-open'), card.classList.contains('is-selected')]);
      card.classList.toggle('is-open', wasThisOne);
      card.classList.toggle('is-selected', wasThisOne);
    });

    // Commit the rewound state as the "before" style of the coming change.
    void host.offsetHeight;

    requestAnimationFrame(function () {
      host.classList.remove('is-replaying');
      cards.forEach(function (card, i) {
        card.classList.toggle('is-open', target[i][0]);
        card.classList.toggle('is-selected', target[i][1]);
      });
    });
  }

  function normPlan(s) { return (s || '').trim().toLowerCase(); }

  function armPlanSwitchReplay(clickedCard) {
    var openCard = document.querySelector('.rnr-pdp-plan.is-open');
    var openRadio = openCard && openCard.querySelector('.rnr-pdp-plan__radio');
    var prevOpenValue = openRadio ? openRadio.getAttribute('data-rr-plan-value') : '';
    var deadline = performance.now() + 1200; // give up rather than replay a stale state

    (function waitForSwap() {
      if (!document.contains(clickedCard)) return replayPlanSwitch(prevOpenValue);
      if (performance.now() > deadline) return;
      requestAnimationFrame(waitForSwap);
    })();
  }

  function onDocumentClick(event) {
    var thumb = event.target.closest('[data-rnr-thumb]');
    if (thumb) {
      setActiveSlide(thumb.closest('.rnr-pdp-gallery'), thumb.getAttribute('data-rnr-thumb'));
      return;
    }

    var navBtn = event.target.closest('[data-rnr-gallery-prev], [data-rnr-gallery-next]');
    if (navBtn) {
      var gallery = navBtn.closest('.rnr-pdp-gallery');
      if (!gallery) return;
      var slides = gallery.querySelectorAll('[data-rnr-slide]');
      var count = slides.length;
      if (!count) return;
      var current = gallery.querySelector('[data-rnr-slide].is-active');
      var index = current ? parseInt(current.getAttribute('data-rnr-slide'), 10) : 0;
      var isPrev = navBtn.hasAttribute('data-rnr-gallery-prev');
      index = isPrev ? (index - 1 + count) % count : (index + 1) % count;
      setActiveSlide(gallery, index);
      return;
    }

    var accToggle = event.target.closest('[data-rnr-acc-toggle]');
    if (accToggle) {
      var acc = accToggle.closest('[data-rnr-acc]');
      if (!acc) return;
      var wasOpen = acc.classList.contains('is-open');
      var group = acc.closest('.rnr-pdp-accordions');
      if (group) {
        group.querySelectorAll('[data-rnr-acc]').forEach(function (item) {
          item.classList.remove('is-open');
          var toggle = item.querySelector('[data-rnr-acc-toggle]');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        });
      }
      if (!wasOpen) {
        acc.classList.add('is-open');
        accToggle.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    var plan = event.target.closest('.rnr-pdp-plan');
    if (plan) {
      /* The card is a <label>, and label activation FORWARDS a second click to
         its control. So one tap arrives here twice — once on whatever was
         clicked, once on `.rnr-pdp-plan__radio` — and both resolve to the same
         card. That ran the toggle below twice, which cancelled itself: an open
         card could never be closed by tapping it again. It also defeated the
         body guard, since the forwarded click's target is the radio and not
         inside [data-plan-body], so tapping an inclusion line collapsed the
         list it was in.

         Collapse the pair before either check. The forwarded click is
         dispatched in the same task as the original, so a guard cleared on the
         next tick leaves exactly one. Keyed on the card, not the event, so it
         behaves identically for a keyboard activation (which sends only one). */
      if (state.planClickGuard === plan) return;
      state.planClickGuard = plan;
      setTimeout(function () {
        if (state.planClickGuard === plan) state.planClickGuard = null;
      }, 0);

      // A click on an inclusion line would otherwise collapse the very list
      // being read. Safe for collapsed cards: their body is 0fr with
      // overflow:hidden, so it has no clickable area.
      if (event.target.closest('[data-plan-body]')) return;

      var radio = plan.querySelector('.rnr-pdp-plan__radio');
      if (radio && radio.checked) {
        // Already the selected plan — this click can only be toggling the
        // inclusions list open/closed. Same element throughout, so the CSS
        // transition runs on its own.
        plan.classList.toggle('is-open');
      } else {
        // A different plan. The native label click checks the radio, that
        // fires `change`, VariantPicker.selectCombination() re-renders the
        // section, and the server ships the new card already `is-open` — with
        // no transition, because it is a new element. Replay it. No
        // preventDefault: the real selection path is untouched.
        armPlanSwitchReplay(plan);
      }
    }
  }

  function bindDelegatedClicksOnce() {
    if (state.clickBound) return;
    state.clickBound = true;
    document.addEventListener('click', onDocumentClick);
  }

  // ---- Section 2: fade-up reveal ----
  // Lives outside <product-rerender>, safe to (re)bind normally. Re-run-safe
  // per element via a dataset flag, so re-init on `shopify:section:load`
  // never double-observes.
  function initSection2Reveal() {
    if (!('IntersectionObserver' in window)) return; // graceful: stays visible, no reveal

    var targets = document.querySelectorAll('[data-rnr-reveal]');
    if (!targets.length) return;

    // Honour the OS setting: no hidden start state, nothing to reveal.
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    if (!state.revealObserver) {
      state.revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          // Values copied from the prototype's initMotion().
          el.style.transition =
            'opacity 1s cubic-bezier(.2,.8,.2,1), transform 1s cubic-bezier(.2,.8,.2,1)';
          el.style.transitionDelay = (parseInt(el.getAttribute('data-rnr-reveal'), 10) || 0) + 'ms';
          el.style.opacity = '1';
          el.style.transform = 'none';
          state.revealObserver.unobserve(el);
        });
      }, { threshold: 0.08 });
    }

    targets.forEach(function (el) {
      if (el.dataset.rnrRevealBound) return;
      el.dataset.rnrRevealBound = '1';
      // Start state is set FROM JS, so a blocked/failed script leaves the
      // copy visible rather than permanently invisible.
      el.style.opacity = '0';
      el.style.transform = 'translateY(28px)';
      state.revealObserver.observe(el);
    });
  }

  // ---- Scroll-scrubbed effects: section 2 heading fill, section 6 wipe ----
  // One listener drives both — a second scroll listener would just fight this
  // one for the same rAF slot.
  // ponytail: plain scroll listener + rAF throttle; swap for
  // `animation-timeline: view()` once Firefox ships it and this can drop to
  // pure CSS.
  function initScrollScrubOnce() {
    if (state.fillBound) return;
    state.fillBound = true;

    var ticking = false;

    function update() {
      ticking = false;
      var vh = window.innerHeight;
      document.querySelectorAll('[data-rnr-fill]').forEach(function (heading) {
        var rect = heading.getBoundingClientRect();
        var p = (vh * 0.82 - rect.top) / (rect.height + vh * 0.22);
        p = Math.max(0, Math.min(1, p));
        heading.style.setProperty('--f', (p * 100).toFixed(1) + '%');
      });

      // Section 6: RISE → REST wipe. --fp is 0 while the section's top edge is
      // still below the viewport top, and 1 once it has scrolled by its own
      // height minus one viewport — i.e. exactly the span the sticky child is
      // pinned for. Same expression as the prototype's own handler.
      document.querySelectorAll('[data-rnr-formulas]').forEach(function (section) {
        var rect = section.getBoundingClientRect();
        var total = section.offsetHeight - vh;
        var p = Math.max(0, Math.min(1, -rect.top / (total || 1)));
        section.style.setProperty('--fp', p.toFixed(3));
      });

      // Section 8: year timeline.
      //
      // 2026-08-14: the section stopped being a 340vh pinned horizontal
      // scrub and became an ordinary static row (desktop) / vertical list
      // (mobile). The OLD expression `-rect.top / (offsetHeight - vh)` was
      // written for a section TALLER than the viewport; on the new short
      // section `offsetHeight - vh` is negative, which would drive --yp to 0
      // or 1 with nothing in between and the rail would snap rather than
      // fill. The updated prototype uses a viewport-crossing ramp instead,
      // copied verbatim here:
      //
      //   yp = clamp((vh - rect.top) / (rect.height + vh * 0.15), 0, 1)
      //
      // 0 while the section is still fully below the fold, 1 once it has
      // travelled its own height plus a 15%-viewport tail.
      document.querySelectorAll('[data-rnr-year]').forEach(function (section) {
        var rect = section.getBoundingClientRect();
        var span = rect.height + vh * 0.15;
        var p = Math.max(0, Math.min(1, (vh - rect.top) / (span || 1)));
        section.style.setProperty('--yp', p.toFixed(4));

        // Dots light in sequence as the rail passes them. This behaviour is
        // NEW — the previous prototype's dots were static, which is why
        // round 4 deleted the (broken, unreachable) GSAP version and left
        // the `.rr-year-dot.lit` CSS inert. The updated prototype drives it,
        // so the CSS is now reachable again. Threshold and ordering are the
        // prototype's: (index + 0.35) / count.
        var dots = section.querySelectorAll('.rr-year-dot');
        for (var d = 0; d < dots.length; d++) {
          dots[d].classList.toggle('lit', p >= (d + 0.35) / dots.length);
        }
      });
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  function init() {
    bindDelegatedClicksOnce();
    initSection2Reveal();
    initScrollScrubOnce();
  }

  init();
  document.addEventListener('shopify:section:load', init);
})();


/* ##########################################################################

   Merged in on 2026-08-13 (round 4) so the whole RNR PDP ships ONE script.
   Previously assets/rnr-pdp-animations.js + assets/rnr-pdp-sections.js (both
   deleted) and per-section inline <script> blocks in rnr-pdp-compare / -app /
   -closing / -sticky-bar.

   Four near-identical copies of the same IntersectionObserver reveal were
   removed with them — every section's reveal attribute is now
   `data-rnr-reveal`, handled once by initSection2Reveal above. rnr-pdp-closing
   had nothing else in its script, so it lost the script entirely.

   This file is loaded by every RNR PDP section AND by the standalone
   ingredients Page, so nothing here may assume a given section exists.
   ########################################################################## */

/* ---- GSAP controller (arc + ingredients register with it) --------------- */
/* GSAP 3.13 + ScrollTrigger are already loaded globally, async, at
   layout/theme.liquid:164-165. This polls for them rather than loading them. */
/**
 * Global GSAP animation controller.
 * Loaded once via layout/theme.liquid. Every animated section registers
 * itself here via `Theme.Animations.define(name, initFn)` and gets
 * initialized/cleaned up automatically — including in the Theme Editor.
 *
 * NOTE: GSAP + ScrollTrigger load via <script async>, so we cannot assume
 * they exist yet when this file runs. We poll until both are present.
 */
;(function () {
  if (window.Theme && window.Theme.Animations && window.Theme.Animations.__ready) return;

  window.Theme = window.Theme || {};
  var queued = (window.Theme.Animations && window.Theme.Animations._queue) || [];

  var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var definitions = {};
  var contexts = {}; // sectionId -> gsap.context, for cleanup
  var pendingRoots = []; // roots asked to init before GSAP was ready

  function define(name, fn) {
    definitions[name] = fn;
  }

  function initRoot(root) {
    if (!root || root.dataset.animInitialized === 'true') return;

    if (!window.gsap || !window.ScrollTrigger) {
      // GSAP hasn't finished loading yet (async race) — queue and retry later
      if (pendingRoots.indexOf(root) === -1) pendingRoots.push(root);
      return;
    }

    var name = root.getAttribute('data-animate-section');
    var fn = definitions[name];
    if (!fn) return;

    var sectionId = root.getAttribute('data-section-id') || (name + '-' + Math.random().toString(36).slice(2));

    var ctx = gsap.context(function () {
      fn(root, { gsap: gsap, ScrollTrigger: window.ScrollTrigger, prefersReduced: prefersReduced });
    }, root);

    contexts[sectionId] = ctx;
    root.dataset.animInitialized = 'true';
    root.dataset.sectionId = sectionId;
  }

  function initAll(scope) {
    var scopeEl = scope && scope.querySelectorAll ? scope : document;
    scopeEl.querySelectorAll('[data-animate-section]').forEach(initRoot);
  }

  function destroyRoot(root) {
    if (!root) return;
    var sectionId = root.dataset ? root.dataset.sectionId : null;
    if (sectionId && contexts[sectionId]) {
      contexts[sectionId].revert();
      delete contexts[sectionId];
    }
    var idx = pendingRoots.indexOf(root);
    if (idx > -1) pendingRoots.splice(idx, 1);
  }

  function reExecuteScripts(scope) {
    scope.querySelectorAll('script[data-animate-script]').forEach(function (old) {
      var fresh = document.createElement('script');
      for (var i = 0; i < old.attributes.length; i++) {
        fresh.setAttribute(old.attributes[i].name, old.attributes[i].value);
      }
      fresh.textContent = old.textContent;
      old.parentNode.replaceChild(fresh, old);
    });
  }

  // Poll for GSAP + ScrollTrigger since both load with `async` and may
  // arrive after this file, before this file, or in either order.
  var pollAttempts = 0;
  var maxPollAttempts = 250; // ~5s at 20ms intervals

  function pollForGsap() {
    pollAttempts++;

    if (window.gsap && window.ScrollTrigger) {
      if (!window.__gsapPluginsRegistered) {
        gsap.registerPlugin(ScrollTrigger);
        window.__gsapPluginsRegistered = true;
      }
      // Retry any sections that tried to init before GSAP was ready
      var toRetry = pendingRoots.slice();
      pendingRoots.length = 0;
      toRetry.forEach(initRoot);
      return;
    }

    if (pollAttempts < maxPollAttempts) {
      setTimeout(pollForGsap, 20);
    } else {
      console.warn('[Theme.Animations] GSAP failed to load from CDN after 5s — animations skipped.');
    }
  }

  window.Theme.Animations = {
    __ready: true,
    define: define,
    initAll: initAll,
    initRoot: initRoot,
    destroyRoot: destroyRoot
  };

  queued.forEach(function (pair) { define(pair[0], pair[1]); });

  pollForGsap();

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  document.addEventListener('shopify:section:load', function (evt) {
    reExecuteScripts(evt.target);
    initAll(evt.target);
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var root = evt.target.matches('[data-animate-section]')
      ? evt.target
      : evt.target.querySelector('[data-animate-section]');
    destroyRoot(root);
  });

  document.addEventListener('shopify:section:reorder', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:block:select', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });
})();


/* ---- Section animation definitions (arc, ingredients) ------------------ */
/**
 * Combined animation logic for:
 *  - Rise & Rest scrollytelling section  (data-animate-section="rise-rest")
 *  - Ingredients (Rise/Rest) section      (data-animate-section="rr-ingredients")
 *
 * Both register with the global controller — this file does not load or
 * init GSAP itself. Loaded by BOTH sections' .liquid files (see notes below).
 */
;(function () {
  function define(name, fn) {
    if (window.Theme && window.Theme.Animations && window.Theme.Animations.define) {
      window.Theme.Animations.define(name, fn);
    } else {
      window.Theme = window.Theme || {};
      window.Theme.Animations = window.Theme.Animations || { _queue: [] };
      window.Theme.Animations._queue.push([name, fn]);
    }
  }

  // ==========================================================================
  // Rise & Rest — scroll-driven dial/timeline section
  // ==========================================================================
  define('rise-rest', function (root, ctx) {
    var gsap = ctx.gsap;
    var ScrollTrigger = ctx.ScrollTrigger;

    // Respect prefers-reduced-motion: hold a representative mid-state
    // instead of scrubbing the story on scroll.
    if (ctx.prefersReduced) {
      gsap.set(root, { '--cp': 0.5 });
      return;
    }

    // Reproduces the old `animation-range: cover 25% cover 75%` buffer:
    // scroll spends its first/last ~15% holding at the start/end state
    // rather than animating immediately.
   function updateCp(self) {
  var cp = gsap.utils.clamp(0, 1, (self.progress - 0.15) / 0.7);
  gsap.to(root, {
    '--cp': cp,
    duration: 0.3,       // was 0.6 — halved
    ease: 'power2.out',
    overwrite: true
  });
}

ScrollTrigger.create({
  trigger: root,
  start: 'top top',
  end: 'bottom bottom',
  scrub: 0.55,            // was 0.8 — roughly halved back toward original
  onUpdate: updateCp,
  onRefresh: updateCp
});

    // Dashed ring rotation — was `animation: rr-spin 60s linear infinite`
    // var dashed = root.querySelector('.rise-rest__dial-track--dashed');
    // if (dashed) {
    //   gsap.to(dashed, {
    //     rotate: 360,
    //     transformOrigin: '50% 50%',
    //     duration: 60,
    //     ease: 'none',
    //     repeat: -1
    //   });
    // }
  });

  // ==========================================================================
  // Ingredients (Rise/Rest) — marquee tracks + header reveal
  // ==========================================================================
  define('rr-ingredients', function (root, ctx) {
    var gsap = ctx.gsap;

    // ---- Marquee tracks — one per panel (Rise + Rest each have their own) ----
    var tracks = root.querySelectorAll('.rr-ing-track');

    tracks.forEach(function (track) {
      if (ctx.prefersReduced) {
        gsap.set(track, { xPercent: 0 });
        return;
      }

      // xPercent: -50 relative to the track's own width. The track holds
      // the real cards followed by an aria-hidden duplicate set, so -50%
      // lands exactly on the start of the duplicate — same trick the old
      // `translateX(-50%)` keyframe relied on for a seamless loop.
      var tween = gsap.to(track, {
        xPercent: -50,
        duration: 92,
        ease: 'none',
        repeat: -1
      });

      track.addEventListener('mouseenter', function () { tween.pause(); });
      track.addEventListener('mouseleave', function () { tween.play(); });
    });

    // ---- Header reveal (heading + tabs) ----
    // data-reveal="N" = N ms stagger delay.
    var revealEls = root.querySelectorAll('[data-reveal]');
    if (!revealEls.length) return;

    if (ctx.prefersReduced) {
      gsap.set(revealEls, { opacity: 1, y: 0 });
      return;
    }

    revealEls.forEach(function (el) {
      var delayMs = parseFloat(el.getAttribute('data-reveal')) || 0;
      gsap.set(el, { opacity: 0, y: 20 });
      gsap.to(el, {
        opacity: 1,
        y: 0,
        duration: 1,
        delay: delayMs / 1000,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: root,
          start: 'top 80%',
          once: true
        }
      });
    });
  });

})();


/* ---- Section 4: compare table (.tm3) ----------------------------------- */
/* Was keyed to an id built from section.id; now class-based so one copy in a
   shared file serves every instance. The reveal block that used to close this
   function is gone — see the note at the top. */
;(function () {
  function initCompare(root) {
    if (root.dataset.tm3Ready === '1') return;
    root.dataset.tm3Ready = '1';

    var tabs = root.querySelectorAll('.tm3__tab');
    var syncing = false;

    function select(plan, broadcast) {
      if (plan !== 'a' && plan !== 'b') return;
      root.setAttribute('data-tm3-selected', plan);
      for (var i = 0; i < tabs.length; i++) {
        tabs[i].setAttribute('aria-pressed', tabs[i].getAttribute('data-tm3-pick') === plan ? 'true' : 'false');
      }
      if (broadcast && !syncing) {
        document.dispatchEvent(new CustomEvent('rr:plan-change', { detail: { plan: plan, source: root } }));
      }
    }

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var trigger = target.closest('[data-tm3-pick]');
      if (!trigger || !root.contains(trigger)) return;
      /* Links keep navigating — they only set the state on the way out. */
      select(trigger.getAttribute('data-tm3-pick'), true);
    });

    /* Stay in step with the sticky bar, which speaks the same event. */
    document.addEventListener('rr:plan-change', function (event) {
      if (!event.detail || event.detail.source === root) return;
      syncing = true;
      select(event.detail.plan, false);
      syncing = false;
    });
  }

  /* ---- Section 5: app phone (.tf) -------------------------------------- */
  function initApp(root) {
    if (root.dataset.tfReady === '1') return;
    root.dataset.tfReady = '1';

    var count = parseInt(root.getAttribute('data-tf-count'), 10) || 0;
    if (count < 1) return;

    var triggers = root.querySelectorAll('[data-tf-go]');
    var slides = root.querySelectorAll('.tf__slide');
    var caption = root.querySelector('.tf__caption');
    var index = 0;

    function show(next) {
      index = ((next % count) + count) % count;
      root.style.setProperty('--tf-index', index);

      for (var i = 0; i < triggers.length; i++) {
        var on = parseInt(triggers[i].getAttribute('data-tf-go'), 10) === index;
        triggers[i].classList.toggle('is-active', on);
        triggers[i].setAttribute('aria-pressed', on ? 'true' : 'false');
      }
      for (var j = 0; j < slides.length; j++) {
        if (j === index) slides[j].removeAttribute('aria-hidden');
        else slides[j].setAttribute('aria-hidden', 'true');
      }
      if (caption && slides[index]) {
        caption.textContent = slides[index].getAttribute('data-tf-caption') || '';
      }
    }

    var interval = parseInt(root.getAttribute('data-tf-autoplay'), 10) || 0;
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var timer = null;

    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    function start() {
      if (timer || !interval || reduced) return;
      timer = setInterval(function () { show(index + 1); }, interval);
    }

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var trigger = target.closest('[data-tf-go]');
      if (!trigger || !root.contains(trigger)) return;
      show(parseInt(trigger.getAttribute('data-tf-go'), 10) || 0);
      if (navigator.vibrate) navigator.vibrate(8);
      /* Restart the clock so a manual pick isn't yanked away mid-look. */
      stop();
      start();
    });

    /* Swipe, added by the 2026-08-14 prototype update (mobile only there —
       the phone frame carries touch-action: pan-y and pointer handlers).
       Thresholds are the prototype's: a gesture counts only if it moved more
       than 36px horizontally AND was more horizontal than vertical, so a
       normal vertical page scroll started on the phone never flips a slide. */
    var frame = root.querySelector('.tf__screen') || root;
    var sx = 0;
    var sy = 0;

    frame.addEventListener('pointerdown', function (event) {
      sx = event.clientX;
      sy = event.clientY;
    }, { passive: true });

    frame.addEventListener('pointerup', function (event) {
      var dx = event.clientX - sx;
      var dy = event.clientY - sy;
      if (Math.abs(dx) <= 36 || Math.abs(dx) <= Math.abs(dy)) return;
      show(index + (dx < 0 ? 1 : -1));
      if (navigator.vibrate) navigator.vibrate(8);
      stop();
      start();
    }, { passive: true });

    /* Don't burn a timer on a section nobody is looking at. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) start();
          else stop();
        });
      }, { threshold: 0.15 }).observe(root);
    } else {
      start();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop();
      else start();
    });
  }

  /* ---- Section 12: sticky buy bar (.sb) -------------------------------- */
  function initStickyBar(root) {
    if (root.dataset.sbReady === '1') return;
    root.dataset.sbReady = '1';

    /* Marks the document so the stylesheet can suppress the theme's own fixed
       bottom bar. Scoped this way because rnr-pdp.css is also loaded by the
       standalone ingredients Page, which has no bar and must keep the theme's. */
    document.documentElement.classList.add('rnr-has-sticky-bar');

    var tabs = root.querySelectorAll('[data-sb-pick]');
    var syncing = false;

    function select(plan, broadcast) {
      if (plan !== 'a' && plan !== 'b' && plan !== 'c') return;
      root.setAttribute('data-sb-selected', plan);
      for (var i = 0; i < tabs.length; i++) {
        tabs[i].setAttribute('aria-pressed', tabs[i].getAttribute('data-sb-pick') === plan ? 'true' : 'false');
      }
      if (broadcast && !syncing) {
        document.dispatchEvent(new CustomEvent('rr:plan-change', { detail: { plan: plan, source: root } }));
      }
    }

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var trigger = target.closest('[data-sb-pick]');
      if (!trigger || !root.contains(trigger)) return;
      select(trigger.getAttribute('data-sb-pick'), true);
    });

    /* Stay in step with the compare table, which speaks the same event. */
    document.addEventListener('rr:plan-change', function (event) {
      if (!event.detail || event.detail.source === root) return;
      syncing = true;
      select(event.detail.plan, false);
      syncing = false;
    });

    /* --- show / hide --------------------------------------------------- */
    var offset = parseInt(root.getAttribute('data-sb-offset'), 10);
    if (isNaN(offset)) offset = 620;
    var shown = null;
    var queued = false;

    function covered() {
      /* A section pinned to the whole viewport owns the screen — the mobile
         prototype drops the bar rather than cover it, watching exactly
         [data-arc],[data-formulas],[data-year]. Ours opt in with
         [data-rr-pinned]: the arc, formulas and year sections. */
      var pinned = document.querySelectorAll('[data-rr-pinned]');
      for (var i = 0; i < pinned.length; i++) {
        var r = pinned[i].getBoundingClientRect();
        if (r.top <= 1 && r.bottom >= window.innerHeight - 1) return true;
      }
      return false;
    }
// mobile sticky issue
 function ctaVisible() {
      /* While the PDP's own CTA button is anywhere on screen, keep the
         sticky bar hidden — only show it once that button has scrolled
         fully out of view. */
      var cta = document.querySelector('.rnr-pdp-cta');
      if (!cta) return false;
      var r = cta.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    }

//  mobile sticky issue
    function update() {
      queued = false;
//  mobile sticky issue
// before
var on = window.pageYOffset > offset && !covered();

// after
var on = window.pageYOffset > offset && !covered() && !ctaVisible();
//  mobile sticky issue
      if (on === shown) return;
      shown = on;
      root.classList.toggle('is-shown', on);
    }

    function onScroll() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(update);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();

    /* --- end-of-page reserve -------------------------------------------- */
    /* The prototype reserves the bar's landing zone in its OWN footer
       (desktop `32px … 110px`, mobile `26px 20px 150px` — the asymmetry IS
       the reserve). This build uses the theme's footer, so that never came
       across and the bar sat on top of the closing section.

       Set from JS rather than CSS so it cannot leak onto the ingredients Page,
       which loads this stylesheet but has no bar. Safe to do in JS because the
       bar is itself JS-revealed — there is no pre-script state where the
       reserve is needed. Measured, not hardcoded: a CTA label wrapping to two
       lines makes the bar taller than any fixed number. */
    function measure() {
      var h = root.offsetHeight;
      if (!h) return;
      /* setProperty with 'important': one of the store's third-party app
         stylesheets forces body padding and beats a plain inline style —
         measured live, the inline 72px computed as 0px without this. */
      document.body.style.setProperty('padding-bottom', h + 'px', 'important');
    }

    measure();
    window.addEventListener('resize', measure, { passive: true });

    /* 'RR Milling' arrives over the network and is taller than the fallback
       stack, so the first measurement can be short by a few px. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(function () {});
    }
  }

  function initAll() {
    document.querySelectorAll('.tm3').forEach(initCompare);
    document.querySelectorAll('.tf').forEach(initApp);
    document.querySelectorAll('.sb').forEach(initStickyBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();


/* ---- Plan sync: hero variant picker <-> compare table <-> sticky bar ----
   Before this, the three plan controls were three independent widgets. The
   compare table and the sticky bar talked to each other over `rr:plan-change`,
   but neither knew about the hero's <variant-picker> — so picking "3 months"
   in the sticky bar left the add-to-cart button, its label and the cart
   targeting the twelve-month variant. Reported as "the main product button is
   not matching with the variant selected in the rest of the page".

   The hero radios are the single source of truth, because they are the real
   theme.js VariantPicker inputs — checking one and firing `change` runs the
   store's own selectCombination(), which re-renders the section, rewrites the
   hidden `id` input, and updates the CTA label. Nothing here touches the cart
   directly; it only moves the selection to the control that already owns it.

   Everything is keyed on `data-rr-plan-value`, the option value NAME
   ("12 months"), matched case- and whitespace-insensitively — the same rule
   the rest of the PDP uses, because the live product options are lowercase
   while the design copy is title case. Listeners are delegated on `document`
   since <product-rerender> replaces the hero subtree on every change. */
;(function () {
  function norm(s) { return (s || '').trim().toLowerCase(); }

  function heroRadio(value) {
    var radios = document.querySelectorAll('.rnr-pdp-plan__radio[data-rr-plan-value]');
    for (var i = 0; i < radios.length; i++) {
      if (norm(radios[i].getAttribute('data-rr-plan-value')) === value) return radios[i];
    }
    return null;
  }

  function currentPlan() {
    var r = document.querySelector('.rnr-pdp-plan__radio[data-rr-plan-value]:checked');
    return r ? norm(r.getAttribute('data-rr-plan-value')) : null;
  }

  /* Point the compare table and the sticky bar at `value`. Each still owns its
     own a/b vocabulary; this only flips the attribute their CSS keys off. */
  function syncFollowers(value) {
    if (!value) return;
    document.querySelectorAll('[data-rr-plan-value]').forEach(function (el) {
      if (el.classList.contains('rnr-pdp-plan__radio')) return;
      if (norm(el.getAttribute('data-rr-plan-value')) !== value) return;

      var pick = el.getAttribute('data-tm3-pick') || el.getAttribute('data-sb-pick');
      if (!pick) return;
      var root = el.closest('.tm3, .sb');
      if (!root) return;

      root.setAttribute(root.classList.contains('tm3') ? 'data-tm3-selected' : 'data-sb-selected', pick);
      root.querySelectorAll('[data-tm3-pick], [data-sb-pick]').forEach(function (btn) {
        var mine = btn.getAttribute('data-tm3-pick') || btn.getAttribute('data-sb-pick');
        btn.setAttribute('aria-pressed', mine === pick ? 'true' : 'false');
      });
    });
  }

  /* A click anywhere outside the hero drives the hero radio, which drives the
     cart. Links keep navigating — the selection is set on the way out. */
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || typeof t.closest !== 'function') return;
    var trigger = t.closest('[data-rr-plan-value]');
    if (!trigger || trigger.classList.contains('rnr-pdp-plan__radio')) return;

    var want = norm(trigger.getAttribute('data-rr-plan-value'));
    if (!want) return;

    /* Move the followers straight away so the UI never lags the click, even
       if the hero has no matching radio (misconfigured option value). */
    syncFollowers(want);

    var radio = heroRadio(want);
    if (!radio || radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* The hero changed — by a card click, or by VariantPicker re-rendering after
     one. Push that back out to the followers. */
  document.addEventListener('change', function (event) {
    var t = event.target;
    if (!t || !t.matches || !t.matches('.rnr-pdp-plan__radio[data-rr-plan-value]')) return;
    syncFollowers(norm(t.getAttribute('data-rr-plan-value')));
  });

  function align() { syncFollowers(currentPlan()); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', align);
  } else {
    align();
  }
  /* Re-assert after a section reload in the theme editor, and after
     <product-rerender> swaps the hero subtree. */
  document.addEventListener('shopify:section:load', align);
})();
